import { For, type Component, type JSX } from "solid-js";
import * as d3 from "d3";
import { sourcesStore } from "../../../store/sourcesStore";
import { chromaticCellHaloClass } from "../../../utils/chromaticTextReadability";

/**
 * Shared pivoted ("Table Layout: PIVOTED") renderer for the report tables whose rows are
 * one-per-team: metrics run down the left as row headers, teams run across the top as
 * columns. Pages keep their own standard-orientation markup and only swap in this component
 * when the pivot display option is on, so the unpivoted path never changes.
 *
 * Column order is the caller's row order, so whatever sort the page has applied survives
 * the transpose. The sort affordance moves onto the metric row headers (and the corner
 * cell, which sorts by the row label) — clicking a team column header would be meaningless.
 */

/** Read a cell, coercing numeric strings. Blank/absent → null. */
export function getPivotRowValue(
  row: Record<string, unknown>,
  key: string
): number | string | null {
  const v = row[key] ?? row[key.toLowerCase()] ?? row[key.toUpperCase()];
  if (v == null || v === "") {return null;}
  const n = Number(v);
  if (!Number.isNaN(n)) {return n;}
  return String(v);
}

/** One decimal place, matching the standard tables. */
export function formatPivotNum(value: number): string {
  if (Number.isNaN(value)) {return "—";}
  return value.toFixed(1);
}

/**
 * Conditional-formatting class (c0..c6) for a cell. Scales are keyed by metric and computed
 * down the metric across all teams, so they are orientation-independent — the same class
 * lands on the same datum whichever way the table is drawn.
 *
 * Always reads the RAW cell value, never a caller's display resolver: the pages build these
 * scale domains from raw values (recomputeScalesFromData), so feeding a converted value in
 * here — e.g. km against a domain of metres — would collapse every cell to one end of the ramp.
 */
export function pivotCellClassForScale(
  row: Record<string, unknown>,
  key: string,
  scales: Record<string, string>,
  formatted: string[]
): string {
  if (!formatted.includes(key) || !scales[key]) {return "";}
  try {
    const { domain, range } = JSON.parse(scales[key]) as { domain: number[]; range: number[] };
    const scale = d3.scaleLinear().domain(domain).range(range);
    const v = getPivotRowValue(row, key);
    if (v == null || typeof v !== "number" || Number.isNaN(v)) {return "";}
    const x = scale(v);
    return `c${Math.round(Math.max(0, Math.min(6, x)))}`;
  } catch {
    return "";
  }
}

function teamCellStyle(sourceName: string | null | undefined): string | undefined {
  if (sourceName == null || String(sourceName).trim() === "") {return undefined;}
  const color = sourcesStore.getSourceColor(String(sourceName));
  if (!color) {return undefined;}
  return `color: ${color};`;
}

function teamCellHaloClass(sourceName: string | null | undefined): string {
  if (sourceName == null || String(sourceName).trim() === "") {return "";}
  const color = sourcesStore.getSourceColor(String(sourceName));
  return chromaticCellHaloClass(color ?? null, { sourceLabel: sourceName });
}

function formatDelta(delta: number): string {
  if (Number.isNaN(delta)) {return "";}
  const prefix = delta > 0 ? "+" : "";
  return `${prefix}${delta.toFixed(1)}`;
}

function deltaClass(d: number): string {
  return d > 0
    ? "race-summary-delta race-summary-delta-positive"
    : d < 0
      ? "race-summary-delta race-summary-delta-negative"
      : "race-summary-delta";
}

/**
 * TSV for "Copy Table Data" in the pivoted orientation, so a paste matches the screen:
 * first line is the row-label header followed by each row's label, then one line per metric.
 * Callers pass the same per-cell text function their standard-orientation copy uses.
 */
export function buildPivotedTsv(
  rows: Record<string, unknown>[],
  rowLabelKey: string,
  metricKeys: string[],
  getHeader: (key: string) => string,
  cellText: (row: Record<string, unknown>, key: string) => string
): string {
  let text = [getHeader(rowLabelKey), ...rows.map((row) => cellText(row, rowLabelKey))].join("\t") + "\n";
  for (const key of metricKeys) {
    text += [getHeader(key), ...rows.map((row) => cellText(row, key))].join("\t") + "\n";
  }
  return text;
}

export type PivotSortConfig = { key: string; direction: "asc" | "desc" };

export interface PivotedMetricTableProps {
  /** Rows in display order — becomes the left-to-right column order. */
  rows: () => Record<string, unknown>[];
  /** Key holding each row's label (usually "source_name"); its values become the headers. */
  rowLabelKey: string;
  /** Metric keys in display order — becomes the top-to-bottom row order. */
  metricKeys: () => string[];
  /** Header text for the row-label key and each metric key. */
  getHeader: (key: string) => string;
  sortConfig: () => PivotSortConfig;
  /** Fired by the metric row headers and the corner cell. */
  onSort: (key: string) => void;
  columnScales: () => Record<string, string>;
  formattedColumns: () => string[];
  showDeltas: () => boolean;
  /**
   * Per-page value resolution, used for display *and* delta maths. Return a number for
   * delta-capable cells, a string for raw/text cells (which never get a delta), or null
   * for "—". Defaults to `getPivotRowValue`. This is where unit conversions belong (e.g.
   * Training Summary's metres → km) so the delta is expressed in displayed units.
   */
  resolveValue?: (row: Record<string, unknown>, key: string) => number | string | null;
  /** Metric keys that must never show a delta. Default: `["count", "record_count"]`. */
  noDeltaKeys?: readonly string[];
  /** Extra classes on the <table> (page-specific styling hooks). */
  class?: string;
  /** Insight "Show me" / click select: highlight pivoted team columns (headers + value cells). */
  isLabelHighlighted?: (label: string) => boolean;
  /** Click a team column header to toggle selection. */
  onLabelClick?: (label: string) => void;
}

const DEFAULT_NO_DELTA_KEYS = ["count", "record_count"] as const;

const PivotedMetricTable: Component<PivotedMetricTableProps> = (props) => {
  const resolve = (row: Record<string, unknown>, key: string): number | string | null =>
    props.resolveValue ? props.resolveValue(row, key) : getPivotRowValue(row, key);

  const noDelta = (key: string): boolean =>
    (props.noDeltaKeys ?? DEFAULT_NO_DELTA_KEYS).some((k) => k.toLowerCase() === key.toLowerCase());

  const sortMarker = (key: string): string =>
    props.sortConfig().key === key ? (props.sortConfig().direction === "asc" ? "▲" : "▼") : "";

  const isSourceAxis = (): boolean => props.rowLabelKey.toLowerCase() === "source_name";

  const columnLabel = (row: Record<string, unknown>): string => {
    const v = resolve(row, props.rowLabelKey);
    return v != null ? String(v) : "—";
  };

  const labelHighlighted = (label: string): boolean =>
    props.isLabelHighlighted?.(label) === true;

  const renderValueCell = (
    row: Record<string, unknown>,
    key: string,
    deltaReference: Record<string, unknown> | null
  ): JSX.Element => {
    const scaleClass = pivotCellClassForScale(row, key, props.columnScales(), props.formattedColumns());
    const val = resolve(row, key);
    const refVal = deltaReference ? resolve(deltaReference, key) : null;
    const delta =
      deltaReference != null &&
      !noDelta(key) &&
      typeof val === "number" &&
      typeof refVal === "number" &&
      !Number.isNaN(val) &&
      !Number.isNaN(refVal)
        ? val - refVal
        : null;
    const displayText = val != null ? (typeof val === "number" ? formatPivotNum(val) : String(val)) : "—";
    const label = columnLabel(row);
    const highlight = isSourceAxis() && labelHighlighted(label);
    const sourceColor = highlight ? sourcesStore.getSourceColor(label) : null;
    // String + !important so selection beats maneuvers-table td.c0–c6 fills.
    const highlightStyle =
      highlight && sourceColor
        ? `background-color: ${sourceColor} !important; color: #fff !important;`
        : undefined;
    return (
      <td
        class={`centered ${scaleClass}${highlight ? " insight-col-highlight" : ""}`}
        style={highlightStyle}
      >
        {displayText}
        {delta != null && <span class={deltaClass(delta)}>{formatDelta(delta)}</span>}
      </td>
    );
  };

  return (
    <table class={`maneuvers-table maneuvers-table-pivoted${props.class ? ` ${props.class}` : ""}`}>
      <thead>
        <tr>
          <th
            class="centered"
            style={{ cursor: "pointer" }}
            onClick={() => props.onSort(props.rowLabelKey)}
            title={`Sort by ${props.getHeader(props.rowLabelKey)}`}
          >
            {props.getHeader(props.rowLabelKey)} {sortMarker(props.rowLabelKey)}
          </th>
          <For each={props.rows()}>
            {(row) => {
              const label = columnLabel(row);
              const highlight = isSourceAxis() && labelHighlighted(label);
              const sourceColor = highlight ? sourcesStore.getSourceColor(label) : null;
              const clickable = isSourceAxis() && !!props.onLabelClick;
              const headerStyle: string | JSX.CSSProperties | undefined = (() => {
                if (highlight && sourceColor) {
                  const cursor = clickable ? " cursor: pointer;" : "";
                  return `background-color: ${sourceColor} !important; color: #fff !important; font-weight: 600;${cursor}`;
                }
                const teamColor = isSourceAxis() ? teamCellStyle(label) : undefined;
                if (clickable) {
                  return teamColor
                    ? `${teamColor} cursor: pointer;`
                    : { cursor: "pointer" };
                }
                return teamColor;
              })();
              return (
                <th
                  class={`centered${isSourceAxis() ? ` team-cell${highlight ? "" : teamCellHaloClass(label)}` : ""}${highlight ? " insight-col-highlight" : ""}`}
                  style={headerStyle}
                  title={label}
                  onClick={() => {
                    if (clickable && label !== "—") {
                      props.onLabelClick?.(label);
                    }
                  }}
                >
                  {label}
                </th>
              );
            }}
          </For>
        </tr>
      </thead>
      <tbody>
        <For each={props.metricKeys()}>
          {(key) => (
            <tr>
              <th
                scope="row"
                class="centered"
                style={{ cursor: "pointer" }}
                onClick={() => props.onSort(key)}
                title={`Sort ${props.getHeader(props.rowLabelKey).toLowerCase()} by ${props.getHeader(key)}`}
              >
                {props.getHeader(key)} {sortMarker(key)}
              </th>
              <For each={props.rows()}>
                {(row, index) =>
                  renderValueCell(
                    row,
                    key,
                    // Deltas run against the left-most column — the same reference entry the
                    // standard orientation compares against (its first row).
                    props.showDeltas() && index() > 0 ? props.rows()[0] : null
                  )
                }
              </For>
            </tr>
          )}
        </For>
      </tbody>
    </table>
  );
};

export default PivotedMetricTable;
